import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createDefaultScenario,
  mergeScenarioDefaults,
  SCENARIO_STORAGE_KEY,
  LEGACY_SCENARIO_STORAGE_KEY,
  LEGACY_SCENARIO_STORAGE_KEY_V5,
  LEGACY_SCENARIO_STORAGE_KEY_V4,
  LEGACY_SCENARIO_STORAGE_KEY_V3,
  LEGACY_SCENARIO_STORAGE_KEY_V2,
  LEGACY_SCENARIO_STORAGE_KEY_V1,
} from './model/seedDefaults';
import { projectForecast, projectAllScenarios } from './model/projectForecast';
import { pullFromEconomics, pushLogosToEconomics } from './model/syncFromEconomics';
import { resetScenarioPack } from './model/scenarios';
import { shouldBlockPushP50 } from './shouldBlockPushP50';

/** Debounce MC so assumption edits stay interactive (MC was 500×60 on every keystroke). */
const MC_DEBOUNCE_MS = 400;

function loadScenario() {
  try {
    let raw = localStorage.getItem(SCENARIO_STORAGE_KEY);
    if (!raw) {
      for (const legacy of [
        LEGACY_SCENARIO_STORAGE_KEY,
        LEGACY_SCENARIO_STORAGE_KEY_V5,
        LEGACY_SCENARIO_STORAGE_KEY_V4,
        LEGACY_SCENARIO_STORAGE_KEY_V3,
        LEGACY_SCENARIO_STORAGE_KEY_V2,
        LEGACY_SCENARIO_STORAGE_KEY_V1,
      ]) {
        if (localStorage.getItem(legacy)) {
          raw = localStorage.getItem(legacy);
          if (raw) {
            const migrated = mergeScenarioDefaults(JSON.parse(raw));
            localStorage.setItem(SCENARIO_STORAGE_KEY, JSON.stringify(migrated));
            localStorage.removeItem(legacy);
            return migrated;
          }
        }
      }
      const fresh = createDefaultScenario();
      localStorage.setItem(SCENARIO_STORAGE_KEY, JSON.stringify(fresh));
      return fresh;
    }
    const migrated = mergeScenarioDefaults(JSON.parse(raw));
    localStorage.setItem(SCENARIO_STORAGE_KEY, JSON.stringify(migrated));
    return migrated;
  } catch {
    const fresh = createDefaultScenario();
    try {
      localStorage.setItem(SCENARIO_STORAGE_KEY, JSON.stringify(fresh));
    } catch {
      /* storage may be unavailable in private/SSR contexts */
    }
    return fresh;
  }
}

function persistScenario(scenario) {
  try {
    localStorage.setItem(SCENARIO_STORAGE_KEY, JSON.stringify(scenario));
  } catch {
    /* Keep the in-memory scenario usable when storage is unavailable/full. */
  }
}

export function useForecastScenario() {
  const [scenario, setScenarioState] = useState(loadScenario);
  const [syncMessage, setSyncMessage] = useState(null);
  const [mcProjection, setMcProjection] = useState(null);
  const [mcPending, setMcPending] = useState(false);

  const setScenario = useCallback((updater) => {
    setScenarioState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const normalized = mergeScenarioDefaults(next);
      persistScenario(normalized);
      // Invalidate MC projection synchronously so Push cannot send the previous P50.
      if (normalized.monteCarlo?.enabled) {
        setMcProjection(null);
        setMcPending(true);
      } else {
        setMcProjection(null);
        setMcPending(false);
      }
      return normalized;
    });
  }, []);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === SCENARIO_STORAGE_KEY && event.newValue) {
        try {
          setScenarioState(mergeScenarioDefaults(JSON.parse(event.newValue)));
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const deterministicScenario = useMemo(
    () => ({
      ...scenario,
      monteCarlo: { ...scenario.monteCarlo, enabled: false },
    }),
    [scenario]
  );

  // Instant path for editing / sync; MC bands arrive after debounce.
  const liveProjection = useMemo(
    () => projectForecast(deterministicScenario),
    [deterministicScenario]
  );
  const allProjections = useMemo(
    () => projectAllScenarios(deterministicScenario),
    [deterministicScenario]
  );

  useEffect(() => {
    if (!scenario.monteCarlo?.enabled) {
      setMcProjection(null);
      setMcPending(false);
      return undefined;
    }

    setMcPending(true);
    const timer = window.setTimeout(() => {
      // Honor the configured run count. The input and engine share a 50–5000
      // bound; silently replacing a user's value with 250 makes the reported
      // percentile quality untrustworthy.
      const runs = Math.min(5000, Math.max(50, scenario.monteCarlo.runs || 150));
      setMcProjection(
        projectForecast({
          ...scenario,
          monteCarlo: { ...scenario.monteCarlo, runs },
        })
      );
      setMcPending(false);
    }, MC_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [scenario]);

  // While MC is computing/debouncing, show deterministic so edits stay snappy.
  const projection = mcPending || !mcProjection ? liveProjection : mcProjection;

  const activeKey = scenario.activeScenario || 'base';
  const activePack = scenario.packs[activeKey];

  const patchActivePack = useCallback(
    (patch) => {
      setScenario((prev) => ({
        ...prev,
        packs: {
          ...prev.packs,
          [activeKey]: {
            ...prev.packs[activeKey],
            ...patch,
            assumptions: patch.assumptions
              ? { ...prev.packs[activeKey].assumptions, ...patch.assumptions }
              : prev.packs[activeKey].assumptions,
            funnel: patch.funnel
              ? { ...prev.packs[activeKey].funnel, ...patch.funnel }
              : prev.packs[activeKey].funnel,
            cohorts: patch.cohorts
              ? { ...prev.packs[activeKey].cohorts, ...patch.cohorts }
              : prev.packs[activeKey].cohorts,
            channels: patch.channels
              ? {
                  ...prev.packs[activeKey].channels,
                  ...patch.channels,
                  paid: {
                    ...prev.packs[activeKey].channels?.paid,
                    ...patch.channels.paid,
                  },
                  organic: {
                    ...prev.packs[activeKey].channels?.organic,
                    ...patch.channels.organic,
                  },
                }
              : prev.packs[activeKey].channels,
          },
        },
      }));
    },
    [activeKey, setScenario]
  );

  const setActiveScenario = useCallback(
    (key) => setScenario((prev) => ({ ...prev, activeScenario: key })),
    [setScenario]
  );

  const resetPack = useCallback(
    (key) => {
      setScenario((prev) => resetScenarioPack(prev, key));
    },
    [setScenario]
  );

  const resetToDefaults = useCallback(() => {
    const fresh = createDefaultScenario();
    setScenario(fresh);
    return fresh;
  }, [setScenario]);

  const pullEconomics = useCallback(() => {
    const result = pullFromEconomics(scenario);
    if (result.synced) setScenario(result.scenario);
    setSyncMessage(result.message);
    return result;
  }, [scenario, setScenario]);

  const pushWaitingForMc = shouldBlockPushP50({
    mcEnabled: scenario.monteCarlo?.enabled,
    mcPending,
    mcProjection,
  });

  const pushEconomics = useCallback(() => {
    if (scenario.monteCarlo?.enabled) {
      if (mcPending || !mcProjection) {
        const result = {
          ok: false,
          synced: false,
          message: 'Waiting for Monte Carlo P50',
        };
        setSyncMessage(result.message);
        return result;
      }
      const result = pushLogosToEconomics(scenario, mcProjection);
      setSyncMessage(result.message);
      return result;
    }
    const result = pushLogosToEconomics(scenario, liveProjection);
    setSyncMessage(result.message);
    return result;
  }, [scenario, mcPending, mcProjection, liveProjection]);

  const exportJson = useCallback(() => {
    const blob = new Blob([JSON.stringify(scenario, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `ffc-forecast-scenario-${scenario.asOf || 'export'}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [scenario]);

  const persistSyncedActuals = useCallback(
    (syncedActuals) => {
      setScenario((prev) => ({
        ...prev,
        syncedActuals: syncedActuals ?? null,
      }));
    },
    [setScenario]
  );

  const applyLiveCalibration = useCallback(
    ({ openingMrr, logos, signupRatePerMonth, syncedActuals: nextSyncedActuals }) => {
      setScenario((prev) => {
        const key = prev.activeScenario || 'base';
        const pack = prev.packs[key];
        const assumptions = { ...pack.assumptions };
        const funnel = { ...pack.funnel };
        const channels = {
          ...pack.channels,
          organic: { ...pack.channels?.organic },
        };
        if (openingMrr != null) assumptions.openingMrr = openingMrr;
        if (logos != null) assumptions.openingLogos = logos;
        if (signupRatePerMonth != null) {
          funnel.baseMonthlySignups = signupRatePerMonth;
          channels.organic.monthlySignups = signupRatePerMonth;
        }
        return {
          ...prev,
          syncedActuals: nextSyncedActuals ?? prev.syncedActuals ?? null,
          packs: {
            ...prev.packs,
            [key]: {
              ...pack,
              assumptions,
              funnel,
              channels,
            },
          },
        };
      });
      setSyncMessage(
        `Applied available live calibration${openingMrr != null ? `: MRR $${openingMrr}` : ''}` +
          `${logos != null ? `${openingMrr != null ? ',' : ':'} logos ${logos}` : ''}` +
          `${signupRatePerMonth != null ? `${openingMrr != null || logos != null ? ',' : ':'} signups/mo ${signupRatePerMonth}` : ''}.` +
          (nextSyncedActuals?.mrrSeries?.length
            ? ` Stored ${nextSyncedActuals.mrrSeries.length} actuals points.`
            : '')
      );
    },
    [setScenario]
  );

  return {
    scenario,
    setScenario,
    activePack,
    activeKey,
    patchActivePack,
    setActiveScenario,
    resetPack,
    resetToDefaults,
    projection,
    allProjections,
    pullEconomics,
    pushEconomics,
    syncMessage,
    setSyncMessage,
    exportJson,
    persistSyncedActuals,
    applyLiveCalibration,
    mcPending,
    pushWaitingForMc,
  };
}
